import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { auth } from '../../services/firebase';
import colors from '../../assets/theme/colors';
import {
  buildReviewArtifactFormState,
  buildReviewArtifactUpdates,
  fetchReviewArtifactsForCapture,
  resolveReviewArtifact,
} from './captureArtifactReviewService';

const getArtifactTitle = (artifact = {}) => (
  artifact.title || artifact.titlePrefix || artifact.category || artifact.knowledgeType || 'Review item'
);

const getArtifactSummary = (artifact = {}) => (
  artifact.summary || artifact.text || artifact.note || artifact.content || artifact.body || ''
);

const getArtifactTypeLabel = (artifact = {}) => {
  if (artifact.artifactType === 'knowledge_item') return 'Care knowledge';
  if (artifact.artifactType === 'medication_update') return 'Medication update';
  return 'Timeline event';
};

const getArtifactCategory = (artifact = {}) => (
  artifact.knowledgeType || artifact.category || artifact.logCategory || ''
);

const ReviewArtifactCard = ({
  artifact,
  busy = false,
  onApprove,
  onEditApprove,
  onDiscard,
}) => {
  const [form, setForm] = React.useState(() => buildReviewArtifactFormState(artifact));
  const sourceText = artifact.sourceText || artifact.rawSourceText || artifact.originalText || '';
  const isTimeline = artifact.collectionName === 'dailyLogs';

  React.useEffect(() => {
    setForm(buildReviewArtifactFormState(artifact));
  }, [artifact]);

  const updateField = (field) => (event) => {
    setForm((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: '14px',
        border: `1px solid ${alpha(colors.brand.tint, 0.24)}`,
        bgcolor: alpha('#FFFFFF', 0.95),
      }}
    >
      <Stack spacing={1}>
        <Box>
          <Typography sx={{ fontSize: '0.74rem', fontWeight: 850, color: colors.brand.deep }}>
            {getArtifactTypeLabel(artifact)}
          </Typography>
          <Typography sx={{ mt: 0.2, fontSize: '0.95rem', fontWeight: 900, color: colors.semantic.ink }}>
            {getArtifactTitle(artifact)}
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: '0.78rem', color: alpha(colors.semantic.ink, 0.68) }}>
            {[getArtifactCategory(artifact), isTimeline ? artifact.localDate || artifact.entryDayKey : '', isTimeline ? artifact.localTime || artifact.time : '']
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>

        <TextField
          label="Title"
          value={form.title}
          onChange={updateField('title')}
          size="small"
          fullWidth
        />
        <TextField
          label="Summary"
          value={form.summary || getArtifactSummary(artifact)}
          onChange={updateField('summary')}
          size="small"
          fullWidth
          multiline
          minRows={3}
        />
        <TextField
          label={artifact.artifactType === 'knowledge_item' ? 'Knowledge type' : 'Category'}
          value={form.category}
          onChange={updateField('category')}
          size="small"
          fullWidth
        />
        {isTimeline ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label="Date"
              value={form.localDate}
              onChange={updateField('localDate')}
              size="small"
              fullWidth
            />
            <TextField
              label="Time"
              value={form.localTime}
              onChange={updateField('localTime')}
              size="small"
              fullWidth
            />
          </Stack>
        ) : null}

        {sourceText ? (
          <Box
            sx={{
              p: 1,
              borderRadius: '10px',
              bgcolor: alpha(colors.brand.tint, 0.1),
              color: alpha(colors.semantic.ink, 0.72),
              fontSize: '0.78rem',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {sourceText}
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            size="small"
            variant="contained"
            disabled={busy}
            onClick={() => onApprove(artifact)}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 850 }}
          >
            Approve
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => onEditApprove(artifact, form)}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 850 }}
          >
            Edit and approve
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            disabled={busy}
            onClick={() => onDiscard(artifact)}
            sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 850 }}
          >
            Discard
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

const CaptureArtifactReviewModal = () => {
  const [captureId, setCaptureId] = React.useState('');
  const [artifacts, setArtifacts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busyId, setBusyId] = React.useState('');
  const [error, setError] = React.useState('');
  const [allSet, setAllSet] = React.useState(false);
  const open = Boolean(captureId);

  const loadArtifacts = React.useCallback(async (nextCaptureId) => {
    if (!nextCaptureId) return;

    setLoading(true);
    setError('');
    try {
      const nextArtifacts = await fetchReviewArtifactsForCapture(nextCaptureId);
      setArtifacts(nextArtifacts);
      setAllSet(nextArtifacts.length === 0);
    } catch (loadError) {
      setError(loadError?.message || "We couldn't load review items.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    const handleReviewRequest = (event) => {
      const nextCaptureId = event.detail?.captureId || '';
      if (!nextCaptureId) return;

      setCaptureId(nextCaptureId);
      setAllSet(false);
      loadArtifacts(nextCaptureId);
    };

    window.addEventListener('captureez:capture-review-request', handleReviewRequest);
    return () => {
      window.removeEventListener('captureez:capture-review-request', handleReviewRequest);
    };
  }, [loadArtifacts]);

  const close = () => {
    setCaptureId('');
    setArtifacts([]);
    setError('');
    setAllSet(false);
  };

  const resolveArtifact = async (artifact, action, updates = {}) => {
    setBusyId(`${artifact.collectionName}:${artifact.id}`);
    setError('');
    try {
      await resolveReviewArtifact({
        artifact,
        action,
        updates,
        userId: auth.currentUser?.uid || null,
      });

      const remainingArtifacts = artifacts.filter((item) => !(
        item.id === artifact.id && item.collectionName === artifact.collectionName
      ));
      setArtifacts(remainingArtifacts);
      setAllSet(remainingArtifacts.length === 0);

      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
          detail: {
            childId: artifact.childId || null,
            collection: artifact.collectionName,
          },
        }));
      }
    } catch (resolveError) {
      setError(resolveError?.message || "We couldn't update this review item.");
    } finally {
      setBusyId('');
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>Review items</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.2}>
          {error ? <Alert severity="warning">{error}</Alert> : null}
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography sx={{ color: alpha(colors.semantic.ink, 0.68), fontSize: '0.86rem' }}>
                Loading review items...
              </Typography>
            </Box>
          ) : null}
          {!loading && allSet ? (
            <Alert severity="success">All set. No items need review.</Alert>
          ) : null}
          {!loading && !allSet ? artifacts.map((artifact, index) => (
            <React.Fragment key={`${artifact.collectionName}:${artifact.id}`}>
              {index > 0 ? <Divider /> : null}
              <ReviewArtifactCard
                artifact={artifact}
                busy={busyId === `${artifact.collectionName}:${artifact.id}`}
                onApprove={(item) => resolveArtifact(item, 'approve')}
                onEditApprove={(item, form) => resolveArtifact(
                  item,
                  'edit_approve',
                  buildReviewArtifactUpdates(item, form)
                )}
                onDiscard={(item) => resolveArtifact(item, 'discard')}
              />
            </React.Fragment>
          )) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} sx={{ textTransform: 'none', fontWeight: 850 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaptureArtifactReviewModal;
